import { motion } from "framer-motion";
import { ArrowUpRight, Smartphone, Laptop, Tablet, Headphones } from "lucide-react";
import { SITE, waLink } from "@/lib/siteConfig";

export default function Hero() {
  return (
    <section
      id="top"
      className="relative pt-28 pb-16 lg:pt-36 lg:pb-24 overflow-hidden"
      data-testid="hero-section"
    >
      {/* Background grid lines */}
      <div className="absolute inset-0 pointer-events-none opacity-[0.04]">
        <div
          className="w-full h-full"
          style={{
            backgroundImage:
              "linear-gradient(#0A0A0A 1px, transparent 1px), linear-gradient(90deg, #0A0A0A 1px, transparent 1px)",
            backgroundSize: "80px 80px",
          }}
        />
      </div>

      <div className="max-w-7xl mx-auto px-6 lg:px-10 relative">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
          {/* Left column: text */}
          <div className="lg:col-span-7">
            <motion.div
              initial={{ opacity: 0, y: 14 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
              className="flex items-center gap-3 mb-6"
            >
              <span className="ez-chip">
                <span className="w-1.5 h-1.5 bg-[#FF3333] rounded-full" />
                {SITE.openLabel}
              </span>
              <span className="stamp hidden sm:inline-block">One stop solution for your Apple device</span>
            </motion.div>

            <motion.h1
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.05 }}
              className="font-display text-5xl sm:text-6xl lg:text-7xl xl:text-8xl font-black tracking-tighter leading-[0.92]"
              data-testid="hero-headline"
            >
            MacBook kamu bermasalah?
              <br />
             <div className="flex flex-col leading-none">
                <span className="text-[#FF3333] text-5xl leading-none tracking-wide"> Tersedia Repair LCD ONLY
              </span>
               <span className="relative inline-block text-2xl font-sans font-medium tracking-widest -mt-1">
                  ● Bergaransi ● Murah ● Ready Stock
                </span>
              </div>
            </motion.h1>

            <motion.p
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.15 }}
              className="mt-8 text-lg sm:text-xl text-[#525252] max-w-2xl leading-relaxed"
              data-testid="hero-subheadline"
            >
              Melayani repair LCD, Logic Board, IC Repair, Flexgate, Dustgate, baterai, keyboard, hingga mati total untuk semua Apple device:
              <br />
              ● MacBook ● iPhone ● iPad ● etc
            </motion.p>

            <motion.div
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.25 }}
              className="mt-10 flex flex-wrap gap-4"
            >
              <a
                href={waLink(SITE.waMessages.bookRepair)}
                target="_blank"
                rel="noopener noreferrer"
                className="btn-ez btn-ez-primary"
                data-testid="hero-cta-primary"
              >
                Book a Repair <ArrowUpRight className="w-4 h-4" />
              </a>
              <a
                href="https://share.google/NMYmip8HSyOT6I7oD"
                target="_blank"
                rel="noopener noreferrer"
                className="btn-ez btn-ez-outline"
                data-testid="hero-cta-secondary"
              >
                Visit the Store
              </a>
            </motion.div>

            {/* Stats row */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.6, delay: 0.4 }}
              className="mt-12 grid grid-cols-3 gap-6 max-w-lg"
            >
              <Stat number="5000+" label="Devices fixed" />
              <Stat number="10+ years" label="Apple Repair Experience" />
              <Stat number="5.0★" label="Customer rating" />
            </motion.div>
          </div>

          {/* Right column: image + floating cards */}
          <div className="lg:col-span-5 relative">
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.7, delay: 0.1 }}
              className="relative aspect-[4/5] overflow-hidden"
            >
              <img
                src="/brand/ez-store.jpeg"
                alt="Service MacBook Pro & MacBook Air rusak di EZ Device Jakarta — ganti LCD, baterai, Flexgate, motherboard"
                className="w-full h-full object-cover mix-blend-multiply"
                data-testid="hero-image"
              />
              {/* Overlay tag - clickable */}
              <a
                href="https://share.google/NMYmip8HSyOT6I7oD"
                target="_blank"
                rel="noopener noreferrer"
                className="absolute top-4 left-4 ez-chip hover:bg-[#FF3333] hover:text-white hover:border-[#FF3333] transition-colors"
                data-testid="hero-visit-us-chip"
              >
                <span>Visit Us</span>
              </a>
            </motion.div>

            {/* Floating service icons */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.5 }}
              className="absolute -bottom-6 -left-6 lg:-left-12 bg-white border border-[#0A0A0A] p-4 grid grid-cols-4 gap-3 shadow-[6px_6px_0_#0A0A0A]"
              data-testid="hero-device-pills"
            >
              <DeviceIcon icon={Smartphone} label="iPhone" />
              <DeviceIcon icon={Laptop} label="MacBook" />
              <DeviceIcon icon={Tablet} label="iPad" />
              <DeviceIcon icon={Headphones} label="Airpods" />
            </motion.div>

            {/* Price tag floating */}
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.5, delay: 0.6 }}
              className="absolute -top-4 -right-2 lg:-right-6 bg-[#FF3333] text-white p-4 w-32"
            >
              <div className="font-display text-3xl font-black leading-none mt-1">Gratis</div>
              <div className="font-mono-ez text-[10px] tracking-widest opacity-90">Biaya Pengecekan</div>
            </motion.div>
          </div>
        </div>
      </div>
    </section>
  );
}

function Stat({ number, label }) {
  return (
    <div>
      <div className="font-display text-3xl sm:text-4xl font-black tracking-tighter leading-none">
        {number}
      </div>
      <div className="font-mono-ez text-[11px] text-[#525252] mt-2 uppercase tracking-widest">
        {label}
      </div>
    </div>
  );
}

function DeviceIcon({ icon: Icon, label }) {
  return (
    <div className="flex flex-col items-center gap-1.5">
      <Icon className="w-5 h-5" strokeWidth={1.8} />
      <span className="font-mono-ez text-[9px] uppercase tracking-wider text-[#525252]">
        {label}
      </span>
    </div>
  );
}
